import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  PageSection,
  Title,
  Gallery,
  Button,
  Dropdown,
  DropdownList,
  DropdownItem,
  MenuToggle
} from '@patternfly/react-core';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';
import { PARTNER_ID, PRODUCT_NAME } from '../../config/partner';

interface AgentConfig {
  id: string;
  title: string;
  description: string;
}

const agentConfigs: AgentConfig[] = [
  {
    id: 'iam-agent',
    title: 'Identity and Access Management (IAM) Agent',
    description: 'Monitor and analyse identity and access management configurations, permissions, and security policies.'
  },
  {
    id: 'asset-management-agent',
    title: 'Asset Management & Uncontrolled Asset Detection Agent',
    description: 'Discover, track, and monitor assets across your environment, including uncontrolled and shadow IT assets.'
  },
  {
    id: 'vulnerability-management-agent',
    title: 'Vulnerability Management Agent',
    description: 'Scan, identify, and prioritise vulnerabilities across your infrastructure and applications.'
  },
  {
    id: 'endpoint-security-agent',
    title: 'Endpoint Security Agent',
    description: 'Monitor endpoint devices for security threats, compliance violations, and suspicious activities.'
  },
  {
    id: 'cloud-security-agent',
    title: 'Cloud Security Agent',
    description: 'Assess and monitor cloud infrastructure security, configurations, and compliance across multiple cloud providers.'
  }
];

export const AgentConfigDemo: React.FunctionComponent = () => {
  useDocumentTitle(`${PARTNER_ID} ${PRODUCT_NAME} | Agent Configuration Demo`);
  
  // Context selector state  
  const [isContextDropdownOpen, setIsContextDropdownOpen] = useState(false);
  const [selectedContext, setSelectedContext] = useState('Cyralight Networks');
  
  const contextOptions = [
    'Cyralight Networks',
    'PrismCrest Digital',
    'Celestixo Holdings',
    'Toravelle Partners'
  ];
  
  // Individual state for each agent
  const [isIamSelected, setIsIamSelected] = useState(false);
  const [isAssetSelected, setIsAssetSelected] = useState(false);
  const [isVulnSelected, setIsVulnSelected] = useState(false);
  const [isEndpointSelected, setIsEndpointSelected] = useState(false);
  const [isCloudSelected, setIsCloudSelected] = useState(false);

  const onChange = (event: React.FormEvent<HTMLInputElement>, checked: boolean) => {
    const name = event.currentTarget.name;

    switch (name) {
      case 'iam-agent':
        setIsIamSelected(checked);
        break;
      case 'asset-management-agent':
        setIsAssetSelected(checked);
        break;
      case 'vulnerability-management-agent':
        setIsVulnSelected(checked);
        break;
      case 'endpoint-security-agent':
        setIsEndpointSelected(checked);
        break;
      case 'cloud-security-agent':
        setIsCloudSelected(checked);
        break;
    }
  };

  const getSelectedAgents = () => {
    const selected: AgentConfig[] = [];
    if (isIamSelected) selected.push(agentConfigs[0]);
    if (isAssetSelected) selected.push(agentConfigs[1]);
    if (isVulnSelected) selected.push(agentConfigs[2]);
    if (isEndpointSelected) selected.push(agentConfigs[3]);
    if (isCloudSelected) selected.push(agentConfigs[4]);
    return selected;
  };

  const getSelectionState = (agentId: string) => {
    switch (agentId) {
      case 'iam-agent': return isIamSelected;
      case 'asset-management-agent': return isAssetSelected;
      case 'vulnerability-management-agent': return isVulnSelected;
      case 'endpoint-security-agent': return isEndpointSelected;
      case 'cloud-security-agent': return isCloudSelected;
      default: return false;
    }
  };

  const onContextToggleClick = () => {
    setIsContextDropdownOpen(!isContextDropdownOpen);
  };

  const onContextSelect = (event: React.MouseEvent<Element, MouseEvent> | undefined, itemId: string | number | undefined) => {
    if (typeof itemId === 'number' || typeof itemId === 'undefined') {
      return;
    }
    setSelectedContext(itemId.toString());
    setIsContextDropdownOpen(false);
    // TODO: Handle context change logic here
    console.log('Context changed to:', itemId);
  };

    const selectedAgents = getSelectedAgents();

  return (
    <PageSection>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <Title headingLevel="h1" size="lg">
          Agent Configuration Demo
        </Title>
      </div>
      <div style={{ marginTop: '1rem', marginBottom: '1.5rem', maxWidth: '300px' }}>
        <p style={{ marginBottom: '1rem', fontSize: '1rem' }}>
          Choose a client organisation
        </p>
        <Dropdown
          isOpen={isContextDropdownOpen}
          onOpenChange={(isOpen) => setIsContextDropdownOpen(isOpen)}
          onOpenChangeKeys={['Escape']}
          toggle={(toggleRef) => (
            <MenuToggle ref={toggleRef} onClick={onContextToggleClick} isExpanded={isContextDropdownOpen}>
              {selectedContext}
            </MenuToggle>
          )}
          onSelect={onContextSelect}
        >
          <DropdownList>
            {contextOptions.map((option, index) => (
              <DropdownItem itemId={option} key={index}>
                {option}
              </DropdownItem>
            ))}
          </DropdownList>
        </Dropdown>
      </div>
      <p style={{ marginBottom: '1.5rem', fontSize: '1rem' }}>
        Please select the agents you would like to deploy
      </p>
      <Gallery hasGutter>
        {agentConfigs.map((agent) => (
          <Card
            key={agent.id}
            id={`selectable-card-${agent.id}`}
            isSelectable
            isSelected={getSelectionState(agent.id)}
          >
            <CardHeader
              selectableActions={{
                selectableActionId: agent.id,
                selectableActionAriaLabelledby: `selectable-card-${agent.id}`,
                name: agent.id,
                onChange,
                hasNoOffset: true
              }}
            >
              <CardTitle>{agent.title}</CardTitle>
            </CardHeader>
            <CardBody>
              <p style={{ margin: 0, color: 'var(--pf-v5-global--Color--200)' }}>
                {agent.description}
              </p>
            </CardBody>
          </Card>
        ))}
      </Gallery>
      {selectedAgents.length > 0 && (
        <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: 'var(--pf-v5-global--BackgroundColor--200)', borderRadius: '4px' }}>
          <Title headingLevel="h3" size="md" style={{ marginBottom: '1rem' }}>
            Selected Agents ({selectedAgents.length})
          </Title>
          <ul style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
            {selectedAgents.map((agent) => (
              <li key={agent.id}>{agent.title}</li>
            ))}
          </ul>
          <Button 
            variant="primary" 
            onClick={() => {
              // TODO: Implement deployment functionality
              console.log('Deploy clicked for agents:', selectedAgents.map(a => a.title));
            }}
          >
            Deploy
          </Button>
        </div>
      )}
    </PageSection>
  );
};
